/**
 * Pure TypeScript Standard MIDI File (.mid / SMF Format 0) encoder
 * Zero external dependencies, 100% client-side compatible
 */

import { MidiExportData } from '../types/midi';

/**
 * Encode an integer into MIDI Variable Length Quantity (VLQ) bytes
 */
function toVariableLengthQuantity(value: number): number[] {
  let buffer = value & 0x7f;
  const bytes: number[] = [];

  while ((value >>= 7)) {
    buffer <<= 8;
    buffer |= (value & 0x7f) | 0x80;
  }

  while (true) {
    bytes.push(buffer & 0xff);
    if (buffer & 0x80) {
      buffer >>= 8;
    } else {
      break;
    }
  }

  return bytes;
}

interface RawMidiEvent {
  tick: number;
  type: 'on' | 'off';
  pitch: number;
  velocity: number;
  channel: number;
}

/**
 * Creates a Standard MIDI File (.mid) binary as a Uint8Array
 */
export function createStandardMidiFile(data: MidiExportData): Uint8Array {
  const ppq = data.ppq || 480;
  const bpm = data.bpm || 120;
  const beats = data.timeSignature?.beats || 4;
  const beatType = data.timeSignature?.beatType || 4;

  // Flatten all notes into Note On and Note Off events
  const events: RawMidiEvent[] = [];

  for (const note of data.notes) {
    events.push({
      tick: Math.max(0, Math.round(note.startTick)),
      type: 'on',
      pitch: Math.max(0, Math.min(127, note.pitch)),
      velocity: Math.max(1, Math.min(127, note.velocity)),
      channel: Math.max(1, Math.min(16, note.channel || 1)),
    });

    events.push({
      tick: Math.max(0, Math.round(note.startTick + Math.max(1, note.durationTicks))),
      type: 'off',
      pitch: Math.max(0, Math.min(127, note.pitch)),
      velocity: 0,
      channel: Math.max(1, Math.min(16, note.channel || 1)),
    });
  }

  // Sort chronologically. If ticks match, turn notes OFF before turning notes ON
  events.sort((a, b) => {
    if (a.tick !== b.tick) return a.tick - b.tick;
    if (a.type === 'off' && b.type === 'on') return -1;
    if (a.type === 'on' && b.type === 'off') return 1;
    return a.pitch - b.pitch;
  });

  const trackBytes: number[] = [];

  // 1. Time Signature Meta Event: delta 0, FF 58 04 nn dd cc bb
  trackBytes.push(0x00); // delta-time
  trackBytes.push(0xff, 0x58, 0x04);
  const dd = Math.round(Math.log2(beatType));
  trackBytes.push(beats, dd, 24, 8);

  // 2. Set Tempo Meta Event: delta 0, FF 51 03 tttttt (microseconds per quarter note)
  const mpqn = Math.round(60000000 / bpm);
  trackBytes.push(0x00); // delta-time
  trackBytes.push(0xff, 0x51, 0x03);
  trackBytes.push((mpqn >> 16) & 0xff, (mpqn >> 8) & 0xff, mpqn & 0xff);

  // 3. Program Change (0x00 = Acoustic Grand Piano) on channel 1
  trackBytes.push(0x00); // delta-time
  trackBytes.push(0xc0, 0x00);

  // 4. Note events
  let lastTick = 0;
  for (const ev of events) {
    const delta = Math.max(0, ev.tick - lastTick);
    lastTick = ev.tick;

    const vlq = toVariableLengthQuantity(delta);
    for (const b of vlq) {
      trackBytes.push(b);
    }

    const channelZeroIndexed = (ev.channel - 1) & 0x0f;
    if (ev.type === 'on') {
      trackBytes.push(0x90 | channelZeroIndexed);
      trackBytes.push(ev.pitch & 0x7f);
      trackBytes.push(ev.velocity & 0x7f);
    } else {
      trackBytes.push(0x80 | channelZeroIndexed);
      trackBytes.push(ev.pitch & 0x7f);
      trackBytes.push(0x00);
    }
  }

  // 5. End of Track: delta 0, FF 2F 00
  trackBytes.push(0x00);
  trackBytes.push(0xff, 0x2f, 0x00);

  // Build full SMF: Header + Track
  const totalLength = 14 + 8 + trackBytes.length;
  const result = new Uint8Array(totalLength);

  // Header Chunk "MThd"
  result[0] = 0x4d; // 'M'
  result[1] = 0x54; // 'T'
  result[2] = 0x68; // 'h'
  result[3] = 0x64; // 'd'
  // Header length = 6
  result[4] = 0x00;
  result[5] = 0x00;
  result[6] = 0x00;
  result[7] = 0x06;
  // Format 0 (single track)
  result[8] = 0x00;
  result[9] = 0x00;
  // Number of tracks = 1
  result[10] = 0x00;
  result[11] = 0x01;
  // Division (PPQ)
  result[12] = (ppq >> 8) & 0xff;
  result[13] = ppq & 0xff;

  // Track Chunk "MTrk"
  result[14] = 0x4d; // 'M'
  result[15] = 0x54; // 'T'
  result[16] = 0x72; // 'r'
  result[17] = 0x6b; // 'k'
  // Track length
  const trackLen = trackBytes.length;
  result[18] = (trackLen >> 24) & 0xff;
  result[19] = (trackLen >> 16) & 0xff;
  result[20] = (trackLen >> 8) & 0xff;
  result[21] = trackLen & 0xff;

  // Track data
  result.set(trackBytes, 22);

  return result;
}

/**
 * Triggers browser download for standard MIDI file
 */
export function downloadMidiFile(data: MidiExportData, filename = 'chord-notator-recording.mid'): void {
  const binary = createStandardMidiFile(data);
  const blob = new Blob([binary as BlobPart], { type: 'audio/midi' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
